import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Coins, Layers, PackageX, TrendingDown, Sparkles } from "lucide-react";
import { BarChart, Bar, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Legend } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, inr, num, actionTone, critTone } from "@/lib/mro";

const KPI = ({ icon: Icon, label, value, sub, tone, testid, delay }) => (
  <div data-testid={testid} className="panel reveal p-4" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-start justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <div className="mt-3 font-display text-2xl font-extrabold tabular tracking-tight">{value}</div>
    <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
  </div>
);

const COLORS = ["#06b6d4", "#0f62fe", "#10b981", "#f59e0b", "#f43f5e", "#8b5cf6", "#14b8a6"];

export default function Overview({ plant }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/overview", { params: { plant } }).then((r) => setData(r.data));
  }, [plant]);

  if (!data)
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );

  const k = data.kpis;
  return (
    <div className="space-y-6" data-testid="overview-page">
      <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-card p-6">
        <div className="grid-canvas pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary pulse-dot" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Executive inventory signal</span>
          </div>
          <h1 className="mt-3 max-w-4xl font-display text-2xl font-extrabold leading-snug tracking-tight sm:text-3xl" data-testid="hero-insight">
            {data.hero_insight}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" data-testid="hero-cta-standardize" onClick={() => navigate("/standardization")}>
              Review duplicate materials <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" data-testid="hero-cta-recommendations" onClick={() => navigate("/recommendations")}>
              Open safety-stock actions
            </Button>
            <Button size="sm" variant="ghost" data-testid="hero-cta-copilot" onClick={() => navigate("/copilot")}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Ask the copilot
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KPI testid="kpi-total-value" delay={0} icon={Layers} tone="text-primary" label="Total MRO inventory" value={inr(k.total_inventory_value)} sub={`${num(k.total_lines)} stocking lines across plants`} />
        <KPI testid="kpi-slow-moving" delay={60} icon={TrendingDown} tone="text-amber-500" label="Excess / slow-moving" value={inr(k.slow_moving_value)} sub={`${k.slow_moving_lines} lines with no movement 12+ mo`} />
        <KPI testid="kpi-risk-exposure" delay={120} icon={AlertTriangle} tone="text-rose-500" label="Downtime-risk exposure" value={inr(k.downtime_risk_exposure)} sub={`${k.at_risk_lines} lines below required cover`} />
        <KPI testid="kpi-capital-release" delay={180} icon={Coins} tone="text-emerald-500" label="Working-capital release" value={inr(k.working_capital_release)} sub={`${inr(k.redistributable_value)} redistributable first`} />
        <KPI testid="kpi-duplicates" delay={240} icon={PackageX} tone="text-violet-500" label="Duplicate descriptions" value={num(k.duplicate_descriptions)} sub={`${k.duplicate_clusters} clusters · ${inr(k.duplicate_value)} · ${k.pending_sme_reviews} need SME`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="panel p-5 lg:col-span-7" data-testid="chart-by-plant">
          <h3 className="font-display text-base font-semibold">Inventory value by plant</h3>
          <p className="text-xs text-muted-foreground">Total holding vs. value with no movement for 12+ months</p>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer>
              <BarChart data={data.by_plant}>
                <XAxis dataKey="plant" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(1)}Cr`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="value" name="Total value" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                <Bar dataKey="slow_value" name="Slow-moving" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="panel p-5 lg:col-span-5" data-testid="chart-ageing">
          <h3 className="font-display text-base font-semibold">Ageing of inventory value</h3>
          <p className="text-xs text-muted-foreground">Bucketed by months since last goods movement</p>
          <div className="mt-4 h-[260px] min-h-[260px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.ageing} dataKey="value" nameKey="bucket" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.ageing.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="panel overflow-hidden" data-testid="top-actions-table">
        <div className="flex items-center justify-between border-b border-border p-5">
          <div>
            <h3 className="font-display text-base font-semibold">Highest-value optimisation actions</h3>
            <p className="text-xs text-muted-foreground">Ranked by working capital released without adding downtime risk</p>
          </div>
          <Button variant="outline" size="sm" data-testid="view-all-recommendations" onClick={() => navigate("/recommendations")}>View all</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Material</th>
                <th className="px-4 py-2.5">Plant</th>
                <th className="px-4 py-2.5">Crit.</th>
                <th className="px-4 py-2.5">Safety stock</th>
                <th className="px-4 py-2.5">Action</th>
                <th className="px-4 py-2.5 text-right">Capital release</th>
              </tr>
            </thead>
            <tbody>
              {data.top_actions.map((r) => (
                <tr key={r.material_id} data-testid={`top-action-row-${r.material_id}`} className="cursor-pointer border-t border-border hover:bg-accent/40" onClick={() => navigate(`/part/${r.material_id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.canonical_name.split("|")[0].trim()} {r.canonical_name.split("|")[2]?.trim()}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{r.material_code} · {r.description}</div>
                  </td>
                  <td className="px-4 py-3">{r.plant}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={critTone(r.criticality)}>{r.criticality}</Badge></td>
                  <td className="px-4 py-3 font-mono tabular">{r.current_safety_stock} → {r.recommended_safety_stock}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={actionTone(r.action)}>{r.action}</Badge></td>
                  <td className="px-4 py-3 text-right font-mono tabular font-semibold text-emerald-500">{inr(r.working_capital_release)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
